/**
 * Desktop Notification Service — Digi Sahyadri HRM
 * Integrates with the native Web Notifications API for PC / Windows Action Center
 * and macOS Notification Center alerts.
 */

import { playNotificationSound, playCelebrationSound } from './soundService';

/**
 * Check if the current browser supports Native Desktop Notifications
 */
export const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

/**
 * Get current permission state: 'default' | 'granted' | 'denied' | 'unsupported'
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

/**
 * Request permission from user to show PC / Windows / Mac desktop notifications.
 * Should ideally be triggered on a user interaction (like Login submit or toggle click).
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.info('[DesktopNotification] Native notifications not supported in this browser.');
    return 'unsupported';
  }

  try {
    if (Notification.permission === 'granted') {
      return 'granted';
    }
    const permission = await Notification.requestPermission();
    console.info('[DesktopNotification] User permission response:', permission);
    return permission;
  } catch (err) {
    console.warn('[DesktopNotification] Error requesting permission:', err);
    return 'denied';
  }
};

/**
 * Display a single native desktop notification in Windows Action Center / macOS Notification Center
 */
export const sendDesktopNotification = ({
  title,
  body,
  icon = '/DIGILOGO.jpg',
  tag,
  url,
  onClick,
  skipSound = false,
}) => {
  // Always trigger the audio chime unless explicitly skipped
  if (!skipSound) {
    try {
      playNotificationSound();
    } catch (_) {}
  }

  if (!isNotificationSupported()) return null;
  if (Notification.permission !== 'granted') return null;

  try {
    const resolvedIcon = icon || '/DIGILOGO.jpg';
    const options = {
      body: body || 'You have a new update in Digi Sahyadri HRM',
      icon: resolvedIcon,
      badge: resolvedIcon,
      tag: tag || undefined,
      renotify: !!tag,
      silent: false, // Let system play standard OS chime
    };

    const notif = new Notification(title || 'Digi Sahyadri HRM', options);

    notif.onclick = (event) => {
      event.preventDefault();
      try {
        window.focus();
      } catch (_) {}

      if (typeof onClick === 'function') {
        onClick();
      } else if (url) {
        const targetUrl = url.startsWith('/')
          ? `${window.location.origin}${url}`
          : url;
        window.location.href = targetUrl;
      }
      try {
        notif.close();
      } catch (_) {}
    };

    // Auto-close after 8 seconds if not dismissed by user
    setTimeout(() => {
      try {
        notif.close();
      } catch (_) {}
    }, 8000);

    return notif;
  } catch (err) {
    console.warn('[DesktopNotification] Failed to show desktop notification:', err);
    return null;
  }
};

/**
 * Send celebration notification for Birthday or Work Anniversary with celebratory audio fanfare
 */
export const sendCelebrationDesktopNotification = (reminder) => {
  if (!reminder) return null;
  const isBirthday = reminder.type === 'birthday';
  const title = isBirthday ? '🎂 Birthday Celebration!' : '🎉 Work Anniversary!';
  const body = reminder.message || (isBirthday ? 'Wishing a very Happy Birthday today!' : 'Celebrating work anniversary today!');

  // Trigger rich celebration fanfare
  try {
    playCelebrationSound();
  } catch (_) {}

  return sendDesktopNotification({
    title,
    body,
    tag: `celebration-${reminder.id || reminder.type}-${new Date().toDateString()}`,
    url: '/reminders',
    skipSound: true,
  });
};

/**
 * Send batch notifications on login for unread requests (staggered to display cleanly)
 */
export const sendBatchDesktopNotifications = (notifications = [], maxCount = 3) => {
  if (!Array.isArray(notifications) || notifications.length === 0) return;
  if (!isNotificationSupported() || Notification.permission !== 'granted') return;

  const sessionKey = 'shown_desktop_notifs_set';
  let shownIds = new Set();
  try {
    const stored = sessionStorage.getItem(sessionKey);
    if (stored) shownIds = new Set(JSON.parse(stored));
  } catch (_) {}

  const unshown = notifications.filter(n => !n.read && !shownIds.has(n.id));
  const toShow = unshown.slice(0, maxCount);

  if (toShow.length > 0) {
    // Play single loud chime for the incoming batch
    try {
      playNotificationSound();
    } catch (_) {}
  }

  toShow.forEach((notif, index) => {
    shownIds.add(notif.id);
    setTimeout(() => {
      const reqType = notif.requestType || 'leave';
      const targetUrl = notif.requestId
        ? `/wrms/request/${reqType}/${notif.requestId}`
        : '/wrms/approvals';

      sendDesktopNotification({
        title: notif.title || 'WRMS Notification',
        body: notif.message || 'You have an unread request notification.',
        tag: `notif-${notif.id}`,
        url: targetUrl,
        skipSound: true,
      });
    }, index * 400); // 400ms stagger between notifications
  });

  try {
    sessionStorage.setItem(sessionKey, JSON.stringify(Array.from(shownIds)));
  } catch (_) {}
};

const desktopNotificationService = {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  sendDesktopNotification,
  sendCelebrationDesktopNotification,
  sendBatchDesktopNotifications,
};

export default desktopNotificationService;
